import { Col, Row, SegmentControl, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Header } from 'screens/Admin/components';
import TeamPicker from 'screens/Admin/screens/QuickQuiz/components/TeamPicker';
import DefaultsSection from './components/DefaultsSection';
import OverridesSection from './components/OverridesSection';
import SessionsSection from './components/SessionsSection';

const TABS = ['defaults', 'overrides', 'sessions'];
// The channel a P4P admin lands on, as the sign-up flow's default team.
const DEFAULT_TEAM_USERNAME = process.env.REACT_APP_DEFAULT_TEAM_USERNAME || 'fctokyo';

/** Defaults and Overrides share the channel's config (GET /admin/goalrush/config). */
const ChannelConfig = ({ teamId, tab }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await Api.get(`/admin/goalrush/config?teamId=${teamId}`, null, { silent: true });
      if (cancelled) return;
      if (res?.data) setConfig(res.data);
      else if (res?.err) dispatch({ type: 'HANDLE_RES', payload: { err: res.err } });
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, dispatch]);

  if (!config) {
    return <Text col={theme.GREY_60}>{t('admin.goalRush.loading')}</Text>;
  }
  return tab === 'defaults' ? (
    <DefaultsSection teamId={teamId} config={config} onConfig={setConfig} />
  ) : (
    <OverridesSection
      teamId={teamId}
      manual={config.manual}
      teamRules={config.resolved}
      onConfig={setConfig}
    />
  );
};

/**
 * GoalRush admin, one channel at a time: its sessions, its always-running default GoalRush, and
 * its override session. A P4P admin picks any channel (FC Tokyo first); a channel owner (a team
 * account, which reaches /admin like Quick Quiz and Polls) manages their own channel only; the
 * backend enforces the same split.
 */
const GoalRush = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const isP4P = !!user?.admin;
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : TABS[0];

  // P4P picks any channel (?team=<id>, rehydrated on refresh, FC Tokyo when unset); an owner is
  // fixed to their own.
  const [team, setTeam] = useState(null);
  const teamIdParam = isP4P ? searchParams.get('team') : user?._id;
  useEffect(() => {
    if (!isP4P) {
      setTeam(null);
      return undefined;
    }
    if (teamIdParam && team?._id === teamIdParam) return undefined;
    let cancelled = false;
    (async () => {
      const res = teamIdParam
        ? await Api.post('/admin/getUser', { userId: teamIdParam }, null, { silent: true })
        : await Api.get(`/users/getByUsername/${DEFAULT_TEAM_USERNAME}`, null, { silent: true });
      if (cancelled || !res?._id) return;
      setTeam(res);
      if (!teamIdParam) updateParams({ team: res._id });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamIdParam, isP4P]);

  const handleTeamChange = (next) => {
    setTeam(next);
    updateParams({ team: next?._id });
  };

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center>
          <Text h3>{t('admin.nav.goalRush')}</Text>
        </Row>
      </Header>

      <Col start noFlex marg="54px 0 0" wid="100%" pad="24px 24px 48px" gap="20px">
        {isP4P && (
          <Col noFlex wid="100%" style={{ maxWidth: '460px' }}>
            <TeamPicker team={team} onChange={handleTeamChange} label={t('admin.goalRush.team')} />
          </Col>
        )}

        {teamIdParam ? (
          <>
            <SegmentControl
              options={TABS.map((key) => t(`admin.goalRush.tabs.${key}`))}
              selectedIdx={TABS.indexOf(tab)}
              handleSelect={(i) => updateParams({ tab: TABS[i] })}
              small
              wid="460px"
              textStyle={{ size: '14px' }}
            />
            {tab === 'sessions' ? (
              <SessionsSection key={teamIdParam} teamId={teamIdParam} />
            ) : (
              <ChannelConfig key={`${teamIdParam}:${tab}`} teamId={teamIdParam} tab={tab} />
            )}
          </>
        ) : (
          <Col
            centerAll
            noFlex
            wid="100%"
            pad="40px 0"
            style={{
              borderRadius: '14px',
              border: `1px dashed ${theme.GREY_30}`,
              background: theme.GREY_10
            }}
          >
            <Text h4 col={theme.GREY_70} marg="0">
              {t('admin.goalRush.noChannel')}
            </Text>
          </Col>
        )}
      </Col>
    </Col>
  );
};

export default GoalRush;
